package com.schoolconnect.app.feature.quickannouncements

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.schoolconnect.app.designsystem.*
import ischoolconnect.sharedui.generated.resources.Res
import ischoolconnect.sharedui.generated.resources.*
import org.jetbrains.compose.resources.painterResource
import kotlinx.coroutines.delay

@Composable
internal fun AddQuickAnnouncementDialog(
    visible: Boolean,
    onDismiss: () -> Unit,
    onSubmit: (QuickAnnouncementDraft) -> Unit,
    isSaving: Boolean = false
) {
    if (!visible) return

    var announcement by remember { mutableStateOf("") }
    var displayUntil by remember { mutableStateOf("") }
    var displayTime by remember { mutableStateOf("") }
    var submitted by remember { mutableStateOf(false) }
    var pendingSubmit by remember { mutableStateOf<QuickAnnouncementDraft?>(null) }
    val minimumDisplayDate = currentAppDateLabel()

    LaunchedEffect(pendingSubmit) {
        pendingSubmit?.let {
            delay(350)
            onSubmit(it)
            pendingSubmit = null
        }
    }

    val announcementError = if (submitted && announcement.isBlank()) "Announcement is required." else ""
    val dateError = when {
        submitted && displayUntil.isBlank() -> "Display until date is required."
        submitted && appDateInputComparable(displayUntil) == null -> "Enter a valid display until date."
        submitted && appDateInputComparable(displayUntil)!! < appDateInputComparable(minimumDisplayDate)!! -> "Display until date cannot be earlier than today."
        else -> ""
    }
    val timeError = if (submitted && displayTime.isBlank()) "Display until time is required." else ""
    val hasMissingRequiredFields = announcement.isBlank() || dateError.isNotBlank() || displayTime.isBlank()

    fun submit(publish: Boolean) {
        submitted = true
        if (!hasMissingRequiredFields) pendingSubmit = QuickAnnouncementDraft(announcement, displayUntil, displayTime, publish)
    }

    AppDialogScaffold(
        onDismiss = { if (!isSaving) onDismiss() },
        size = AppDialogSize.Wide,
        dialogWidthOverride = 1120.dp,
        maxDialogHeight = 900.dp,
        isLoading = isSaving,
        loadingTitle = if (pendingSubmit?.publish == true) "Publishing announcement" else "Saving announcement"
    ) {
        AppDialogHeader(
            title = "Add Announcement",
            subtitle = "Create a short message to display to all parents.",
            onClose = { if (!isSaving) onDismiss() },
            colors = AppDialogDefaultColors
        )
        AppDialogValidationSummary(
            errors = listOf(announcementError, dateError, timeError),
            modifier = Modifier.padding(horizontal = 28.dp, vertical = 8.dp)
        )
        AppDialogBody(
            compactBreakpoint = 820.dp,
            horizontalPadding = 28.dp,
            verticalPadding = 20.dp
        ) { compact ->
            AppDialogFieldColumn(
                label = "Announcement",
                required = true,
                error = announcementError,
                colors = AppDialogDefaultColors
            ) {
                AppDialogTextField(
                    value = announcement,
                    onValueChange = { announcement = it },
                    placeholder = "Enter a short announcement",
                    colors = AppDialogDefaultColors,
                    error = announcementError,
                    singleLine = false,
                    fieldHeight = 160.dp,
                    maxLength = 200,
                    trailingIcon = { AppText("${announcement.length} / 200", color = AppMuted, style = androidx.compose.material3.MaterialTheme.typography.labelSmall) }
                )
            }

            AppDialogResponsiveFieldColumns(
                compact = compact,
                compactContent = {
                    AppDialogFieldColumn("Display Until", required = true, error = dateError, colors = AppDialogDefaultColors) {
                        AppDialogDateField(displayUntil, { displayUntil = it }, error = dateError, colors = AppDialogDefaultColors, minimumDate = minimumDisplayDate)
                    }
                    AppDialogFieldColumn("Time", required = true, error = timeError, colors = AppDialogDefaultColors) {
                        AppDialogTimeField(displayTime, { displayTime = it }, error = timeError, colors = AppDialogDefaultColors)
                    }
                },
                firstColumn = {
                    AppDialogFieldColumn("Display Until", required = true, error = dateError, colors = AppDialogDefaultColors) {
                        AppDialogDateField(displayUntil, { displayUntil = it }, error = dateError, colors = AppDialogDefaultColors, minimumDate = minimumDisplayDate)
                    }
                },
                secondColumn = {
                    AppDialogFieldColumn("Time", required = true, error = timeError, colors = AppDialogDefaultColors) {
                        AppDialogTimeField(displayTime, { displayTime = it }, error = timeError, colors = AppDialogDefaultColors)
                    }
                }
            )

            Row(
                Modifier
                    .fillMaxWidth()
                    .background(AppInfoBg, RoundedCornerShape(8.dp))
                    .border(1.dp, AppInfoBorder, RoundedCornerShape(8.dp))
                    .padding(horizontal = 14.dp, vertical = 12.dp),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(10.dp)
            ) {
                Icon(painterResource(Res.drawable.info_circle), "Information", tint = AppInfoText, modifier = Modifier.size(20.dp))
                AppText("This announcement will become active immediately after publishing and will be visible to all parents until the selected date and time.", color = AppNavy, style = androidx.compose.material3.MaterialTheme.typography.bodyMedium)
            }
        }
        AppDialogDivider()
        AppDialogFooter(
            actions = listOf(
                AppDialogFooterAction("Cancel", { if (!isSaving) onDismiss() }, enabled = !isSaving),
                AppDialogFooterAction(if (isSaving) "Saving..." else "Save Draft", { submit(false) }, enabled = !isSaving),
                AppDialogFooterAction(if (isSaving) "Publishing..." else "Publish", { submit(true) }, style = AppDialogActionStyle.Primary, enabled = !isSaving)
            ),
            leadingContent = {
                AppDialogFooterButton(
                    action = AppDialogFooterAction("Preview", onClick = {}, width = 140.dp, enabled = !isSaving),
                    modifier = Modifier.width(140.dp)
                )
            }
        )
    }
}
